import { normaliseLegacyMarker } from "@/src/core/engine/markerObjects";
import type { Language } from "@/src/i18n/language";
import type {
  AnnotationKind,
  MapDocument,
  MapMode,
  StampAssetId,
  TerrainBrush,
  Viewport,
} from "@/src/types/map";

/**
 * Persisted-record schema version. Bump this whenever the shape of a stored
 * record changes, and add the matching migrator to MIGRATIONS below.
 */
export const CURRENT_SCHEMA_VERSION = 4;

/**
 * A saved map.
 *
 * `document` is the JSON export format verbatim, so the file you download and
 * the record we store stay the same thing. The export format alone is lossy as
 * a persisted shape though: it carries no viewport and no room counter, so
 * reopening a map would forget where you were looking and restart numbering.
 * Those two live in the envelope. See DECISIONS.md D-014.
 */
export interface PersistedMap {
  id: string;
  schemaVersion: number;
  createdAt: number;
  updatedAt: number;
  document: MapDocument;
  viewport: Viewport;
  nextRoomNumber: number;
}

/** Editor preferences. App-scoped, not map-scoped — they follow the user, not the map. */
export interface PersistedPreferences {
  language: Language;
  selectedStampAsset: StampAssetId;
  selectedTerrain: TerrainBrush;
  repeatPlacement: boolean;
  annotationKind: AnnotationKind;
  annotationText: string;
  annotationFontSize: number;
  annotationColor: string;
}

/** Row shown in the map library list. Derived, never stored. */
export interface MapSummary {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  gridType: MapMode;
  objectCount: number;
  regionCount: number;
  hexCount: number;
}

export const summarise = (record: PersistedMap): MapSummary => ({
  id: record.id,
  title: record.document.meta.title,
  createdAt: record.createdAt,
  updatedAt: record.updatedAt,
  gridType: record.document.meta.gridType,
  objectCount: record.document.objects.length,
  regionCount: record.document.geometry.floorPolygons.length,
  hexCount: record.document.geometry.hexCells?.length ?? 0,
});

/** A record as read off disk, before it is known to be current. */
export type StoredRecord = Record<string, unknown> & { schemaVersion: number };

/** Upgrades a record FROM the keyed version TO the next one. */
export type Migrator = (record: StoredRecord) => StoredRecord;

/**
 * Migration scaffold.
 *
 * Keyed by the version a migrator upgrades FROM. migrateRecord() stamps the new
 * schemaVersion itself, so a migrator only has to transform the payload.
 */
export const MIGRATIONS: Record<number, Migrator> = {
  /**
   * v3 -> v4: the marker & furniture family absorbs six stamps that used to be
   * placed a different way (pillar, statue, brazier and the three chests), so
   * each of them now has exactly one representation. The mapping itself lives in
   * markerObjects.ts because the JSON import path needs the same rewrite.
   */
  3: (record) => {
    const document = record.document as { objects?: Array<Record<string, unknown>> } | undefined;
    if (!document || !Array.isArray(document.objects)) return record;
    return {
      ...record,
      document: { ...document, objects: document.objects.map(normaliseLegacyMarker) },
    };
  },
  /**
   * v1 -> v2: the door & passage family. Before v2 a door was just
   * `assetId: "door"` with no variant; every such object becomes an explicit
   * single door so the wall catalogue has something concrete to resolve.
   */
  1: (record) => {
    const document = record.document as { objects?: Array<Record<string, unknown>> } | undefined;
    if (!document || !Array.isArray(document.objects)) return record;
    return {
      ...record,
      document: {
        ...document,
        objects: document.objects.map((object) =>
          object?.assetId === "door" && object.wallType === undefined
            ? { ...object, wallType: "single" }
            : object,
        ),
      },
    };
  },

  /**
   * v2 -> v3: the vertical-movement family. The old catalogue had a single
   * untyped "stairs" stamp with no direction at all, so every one becomes the
   * documented DEFAULT_FLOOR_TYPE. Either direction would be arbitrary; the
   * choice lives in floorObjects.ts where it is visible, and the picker can
   * flip any migrated stair in one click.
   */
  2: (record) => {
    const document = record.document as { objects?: Array<Record<string, unknown>> } | undefined;
    if (!document || !Array.isArray(document.objects)) return record;
    return {
      ...record,
      document: {
        ...document,
        objects: document.objects.map((object) =>
          object?.assetId === "stairs" && object.floorType === undefined
            ? { ...object, floorType: "stairs_down" }
            : object,
        ),
      },
    };
  },
};

export class SchemaVersionError extends Error {}

/**
 * Walks a stored record up to the target schema version, one step at a time.
 * Migrations are injectable so the scaffold itself is testable before a real
 * migration exists.
 */
export const migrateRecord = (
  record: StoredRecord,
  migrations: Record<number, Migrator> = MIGRATIONS,
  target: number = CURRENT_SCHEMA_VERSION,
): StoredRecord => {
  if (!record || typeof record !== "object") {
    throw new SchemaVersionError("Stored record is not an object.");
  }
  const startingVersion = record.schemaVersion;
  if (!Number.isInteger(startingVersion) || startingVersion < 1) {
    throw new SchemaVersionError(
      `Stored record has an unusable schemaVersion: ${String(startingVersion)}`,
    );
  }
  if (startingVersion > target) {
    throw new SchemaVersionError(
      `This map was saved by a newer version of Dungeon Builder (schema v${startingVersion}, this build understands v${target}).`,
    );
  }

  let current = record;
  for (let version = startingVersion; version < target; version += 1) {
    const migrator = migrations[version];
    if (!migrator) {
      throw new SchemaVersionError(`No migration registered from schema v${version} to v${version + 1}.`);
    }
    current = { ...migrator(current), schemaVersion: version + 1 };
  }
  return current;
};
